<card
    id="travelInformation"
    [title]="'Travel Information'"
    [isLoading]="isLoading()"
    [(mode)]="linkedMode"
    [changeModeFn]="changeModeFn"
    [showEditSaveButton]="showSwitchModeButton()"
    [showSharedContent]="true"
    (save)="save()"
>
    @if (showBookButton() && linkedMode() === 'view') {
        <card-header-additional-content>
            <mds-button
                [id]="'bookViaGetthere'"
                (clickEvent)="bookViaGetThereForPatient.emit()"
                [label]="'Book via GetThere'"
                [variant]="'outline'"
            />
        </card-header-additional-content>
    }

    <card-shared-content>
        @if (!isLoading() && study()) {
            <div class="flex flex-col gap-medpace">
                <medpace-caregiver-checkbox-group
                    [mode]="linkedMode()"
                    [formGroup]="formGroup().controls.accompanyingCaregivers"
                    (bookViaGetThereForCaregiver)="bookViaGetThereForCaregiver.emit($event)"
                />
                <mds-divider />
                @for (error of formGroup().controls.travelDetails.errors | keyvalue; track error.key) {
                    <medpace-alert
                        [appearance]="'danger'"
                        [content]="error.value"
                        [attr.data-cy]="'travelDetailsAlert'"
                    />
                }
                @if (linkedMode() === 'edit') {
                    @for (alert of alerts$ | async; track alert) {
                        <medpace-alert [appearance]="alert.type" [content]="alert.content" />
                    }
                }

                <div id="travel-information-tabs">
                    <mds-tab-group
                        #travelTabGroup
                        data-cy="travel-tab-group"
                        [headerPosition]="'above'"
                        [stretchTabs]="false"
                        [tabAlign]="'center'"
                        [preserveContent]="false"
                        [fitInkBarToContent]="false"
                        [dynamicHeight]="true"
                        [disablePagination]="false"
                        [animationDuration]="'0ms'"
                        [selectedIndex]="initialSelectedTabIndex$ | async"
                    >
                        @let flightEnabled = flightEnabled$ | async;
                        <mds-tab
                            [disabled]="flightEnabled === false"
                            #flightTab
                            [label]="'Flights'"
                            formErrorHighlighter
                            [forms]="[
                                formGroup().controls.travelDetails.controls.flight.controls.needsTravel,
                                formGroup().controls.travelDetails.controls.flight.controls.travel.controls
                                    .requestDetails,
                            ]"
                            [filterContent]="flightTab.label()"
                        >
                            <medpace-flight-travel-request-tab
                                [mode]="linkedMode()"
                                [study]="study()!"
                                [formGroup]="formGroup().controls.travelDetails.controls.flight"
                            />
                        </mds-tab>
                        @let trainEnabled = trainEnabled$ | async;
                        <mds-tab
                            [disabled]="trainEnabled === false"
                            #trainTab
                            [label]="'Train'"
                            formErrorHighlighter
                            [forms]="[
                                formGroup().controls.travelDetails.controls.train.controls.needsTravel,
                                formGroup().controls.travelDetails.controls.train.controls.travel.controls
                                    .requestDetails,
                            ]"
                            [filterContent]="trainTab.label()"
                        >
                            <medpace-train-travel-request-tab
                                [mode]="linkedMode()"
                                [study]="study()!"
                                [formGroup]="formGroup().controls.travelDetails.controls.train"
                            />
                        </mds-tab>
                        @let lodgingEnabled = lodgingEnabled$ | async;
                        <mds-tab
                            [disabled]="lodgingEnabled === false"
                            #lodgingTab
                            [label]="'Lodging'"
                            formErrorHighlighter
                            [forms]="[
                                formGroup().controls.travelDetails.controls.lodging.controls.needsTravel,
                                formGroup().controls.travelDetails.controls.lodging.controls.travel.controls
                                    .requestDetails,
                            ]"
                            [filterContent]="lodgingTab.label()"
                        >
                            <medpace-lodging-travel-request-tab
                                [mode]="linkedMode()"
                                [study]="study()!"
                                [formGroup]="formGroup().controls.travelDetails.controls.lodging"
                            />
                        </mds-tab>
                        @let rentalCarEnabled = rentalCarEnabled$ | async;
                        <mds-tab
                            [disabled]="rentalCarEnabled === false"
                            #rentalCarTab
                            [label]="'Rental Car'"
                            formErrorHighlighter
                            [forms]="[
                                formGroup().controls.travelDetails.controls.rentalCar.controls.needsTravel,
                                formGroup().controls.travelDetails.controls.rentalCar.controls.travel.controls
                                    .requestDetails,
                            ]"
                            [filterContent]="rentalCarTab.label()"
                        >
                            <medpace-rental-car-travel-request-tab
                                [mode]="linkedMode()"
                                [study]="study()!"
                                [formGroup]="formGroup().controls.travelDetails.controls.rentalCar"
                            />
                        </mds-tab>
                        @let carServiceEnabled = carServiceEnabled$ | async;
                        <mds-tab
                            [disabled]="carServiceEnabled === false"
                            #carServiceTab
                            [label]="'Car Service'"
                            formErrorHighlighter
                            [forms]="[
                                formGroup().controls.travelDetails.controls.carService.controls.needsTravel,
                                formGroup().controls.travelDetails.controls.carService.controls.travel.controls
                                    .requestDetails,
                            ]"
                            [filterContent]="carServiceTab.label()"
                        >
                            <medpace-car-service-travel-request-tab
                                [mode]="linkedMode()"
                                [study]="study()!"
                                [formGroup]="formGroup().controls.travelDetails.controls.carService"
                            />
                        </mds-tab>
                        <!-- other tab is always selectable, hence no there is no
                        [disabled]="(otherEnabled$ | async) === false"
                        -->
                        <mds-tab
                            #otherTab
                            [label]="'Other'"
                            formErrorHighlighter
                            [forms]="[
                                formGroup().controls.travelDetails.controls.other.controls.needsTravel,
                                formGroup().controls.travelDetails.controls.other.controls.travel.controls
                                    .requestDetails,
                            ]"
                            [filterContent]="otherTab.label()"
                        >
                            <medpace-other-travel-request-tab
                                [mode]="linkedMode()"
                                [study]="study()!"
                                [formGroup]="formGroup().controls.travelDetails.controls.other"
                            />
                        </mds-tab>
                    </mds-tab-group>
                </div>
                <div>
                    @if (isUpdate()) {
                        <mds-divider />
                        <!-- [class.hidden] instead of @if - view changes faster and formgroup is validated correctly -->
                        <mds-expansion-panel
                            [class.hidden]="!(linkedMode() === 'edit')"
                            class="expansion-panel-request !border-y-0"
                            [attr.data-cy]="'expense-reporting-panel'"
                            [panelTitle]="'Expense Reporting'"
                            [expanded]="expenseReportingPanelExpandedSubject | async"
                            (openedEvent)="expenseReportingPanelExpandedSubject.next(true)"
                            (closedEvent)="expenseReportingPanelExpandedSubject.next(false)"
                        >
                            <div class="flex flex-col gap-medpace">
                                <div>
                                    <mds-button
                                        (clickEvent)="fileDropInput.click()"
                                        [label]="'Upload multiple receipts'"
                                        [variant]="'outline'"
                                    />

                                    <input
                                        class="hidden"
                                        type="file"
                                        #fileDropInput
                                        [accept]="accept"
                                        [multiple]="true"
                                        (change)="onMultipleFileUpload($event); fileDropInput.value = ''"
                                    />
                                </div>
                                <medpace-alert
                                    [class.hidden]="formGroup().controls.receipts.controls.length !== 0"
                                    [content]="'To add an expense click Add expense button or Upload multiple receipts button'"
                                    [appearance]="'highlight'"
                                />

                                @for (
                                    receiptFormGroup of formGroup().controls.receipts.controls;
                                    let index = $index;
                                    track receiptFormGroup
                                ) {
                                    <medpace-receipt-edit
                                        [index]="index"
                                        [attr.data-cy]="'travel-receipt-' + index"
                                        [formGrp]="receiptFormGroup"
                                        [model]="receiptFormGroup.model"
                                        [isEditMode]="linkedMode() === 'edit'"
                                        (removeEvent)="removeReceipt($event)"
                                    />
                                }

                                <mds-button
                                    (clickEvent)="addReceiptClick()"
                                    [color]="'primary'"
                                    [variant]="'outline'"
                                    [id]="'add-receipt-btn'"
                                    data-cy="add-receipt-btn"
                                    [label]="'Add expense'"
                                />
                            </div>
                        </mds-expansion-panel>

                        @if (request()) {
                            @let receipts = request().receipts;
                            <mds-expansion-panel
                                [class.hidden]="!(linkedMode() === 'view')"
                                class="expansion-panel-request !border-y-0"
                                [panelTitle]="'Expense Reporting'"
                                [attr.data-cy]="'expense-reporting-panel'"
                                [expanded]="expenseReportingPanelExpandedSubject | async"
                                (openedEvent)="expenseReportingPanelExpandedSubject.next(true)"
                                (closedEvent)="expenseReportingPanelExpandedSubject.next(false)"
                            >
                                <div class="flex flex-col gap-medpace">
                                    @if (receipts.length > 0) {
                                        @for (receipt of receipts; let index = $index; track receipt) {
                                            <medpace-receipt-view
                                                [index]="index"
                                                [attr.data-cy]="'travel-receipt-' + index"
                                                [model]="receipt"
                                            />
                                        }
                                    } @else {
                                        <p class="">There are no expenses to be displayed.</p>
                                    }
                                </div>
                            </mds-expansion-panel>
                        }
                    }
                </div>
            </div>
        }
    </card-shared-content>
</card>
